// Runtime resolution for the Events "Variables" block (editor: VariablesChunk).
//
// A variables chunk assigns key -> typed value pairs into the variable store.
// It is applied BEFORE every other chunk in the same event (first in the
// stack). Keys may be nested/templated paths (`test.test[0]`,
// `test.test[{{this.id}}]`); values support {{template}} references including
// `{{this.param}}` and recursive `{{a[{{b}}]}}`.

import { parseIfJSON } from './base';
import { getGlobalVariable, isGlobalVariablePath } from './globalVariableHelper';

export interface VariableRow { key: string; value: string; type?: string; valueOriginal?: string; keyOriginal?: string }
export interface VariablesChunkShape { variables: VariableRow[] }

export const isVariablesChunk = (chunk: any): chunk is VariablesChunkShape => !!chunk && 'variables' in chunk && Array.isArray(chunk.variables);

// Parse a path supporting both dot and bracket notation, e.g.
// `a.b[0].c` -> ['a','b','0','c']. Mirrors the bracket-aware parser used by
// the merge-style saga so reads and writes agree on path semantics.
export const parsePath = (pathStr: string): string[] => {
  const keys: string[] = [];
  let current = '';
  let inBrackets = false;
  let bracketDepth = 0;

  for (let i = 0; i < pathStr.length; i++) {
    const char = pathStr[i];
    if (char === '[') {
      if (inBrackets) {
        bracketDepth++;
        current += char;
      } else {
        if (current) { keys.push(current); current = ''; }
        inBrackets = true;
      }
    } else if (char === ']') {
      if (bracketDepth > 0) {
        bracketDepth--;
        current += char;
      } else {
        inBrackets = false;
        if (current) {
          let key = current;
          if ((key.startsWith('"') && key.endsWith('"')) || (key.startsWith("'") && key.endsWith("'"))) {
            key = key.slice(1, -1);
          }
          keys.push(key);
          current = '';
        }
      }
    } else if (char === '.' && !inBrackets) {
      if (current) { keys.push(current); current = ''; }
    } else {
      current += char;
    }
  }
  if (current) keys.push(current);
  return keys;
};

const getByPath = (obj: any, path: string): any => {
  if (!path || obj === null || obj === undefined) return undefined;
  return parsePath(path).reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    if (!isNaN(Number(key))) {
      const numKey = Number(key);
      return Array.isArray(acc) ? acc[numKey] : acc[key];
    }
    return acc[key];
  }, obj);
};

// Set a value at a bracket/dot path, creating intermediate containers. An
// intermediate is an array when the next segment is a numeric index.
export const setByPath = (target: Record<string, any>, path: string, value: any): void => {
  const keys = parsePath(path);
  if (!keys.length) return;
  let current: any = target;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextIsIndex = !isNaN(Number(keys[i + 1]));
    if (current[key] === null || typeof current[key] !== 'object') {
      current[key] = nextIsIndex ? [] : {};
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
};

// Resolve a single path token to its value from the available contexts.
const resolveToken = (rawPath: string, varsCtx: any, thisCtx: any): any => {
  const path = rawPath.trim();
  if (path === 'this') return thisCtx;
  if (path.startsWith('this.')) return getByPath(thisCtx, path.slice(5));
  if (isGlobalVariablePath(path)) return getGlobalVariable(path);
  return getByPath(varsCtx, path);
};

// Resolve {{...}} templates inner-to-outer so recursive references like
// `{{a[{{b}}]}}` work: the innermost token (one containing no other braces) is
// replaced first, after which the outer token becomes resolvable. Loops until
// no innermost token remains (guarded against pathological input).
export const resolveTemplateRecursive = (input: string, varsCtx: any, thisCtx: any): string => {
  if (typeof input !== 'string') return input;
  let str = input;
  let guard = 0;
  const innermost = /\{\{([^{}]*)\}\}/;
  while (innermost.test(str) && guard < 25) {
    str = str.replace(/\{\{([^{}]*)\}\}/g, (_whole, expr) => {
      const val = resolveToken(expr, varsCtx, thisCtx);
      if (val === undefined || val === null) return '';
      return typeof val === 'object' ? JSON.stringify(val) : String(val);
    });
    guard++;
  }
  return str;
};

// Convert a resolved value to its declared type. Mirrors the action saga's
// convertToType so variables and request params coerce identically.
export const convertToType = (value: any, type?: string): any => {
  if (!type || value === null || value === undefined) return value;
  switch (type.toLowerCase()) {
    case 'string':
      return String(value);
    case 'number': {
      const num = Number(value);
      return isNaN(num) ? String(value) : num;
    }
    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const lower = value.toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;
      }
      return Boolean(value);
    case 'array':
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [value];
        } catch { return [value]; }
      }
      return [value];
    case 'object':
      if (typeof value === 'object') return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return typeof parsed === 'object' && parsed !== null ? parsed : { value };
        } catch { return { value }; }
      }
      return { value };
    default:
      return String(value);
  }
};

// Build the merged data object for one or more variables chunks. Each row's
// key (templates + nesting resolved) maps to its typed, template-resolved
// value. The result is merged into the variable store by the caller.
export const buildVariablesData = (
  chunks: VariablesChunkShape[],
  ctx: { variables?: any; localVariables?: any; thisContext?: any; formValues?: any },
): Record<string, any> => {
  const parsedLocal = typeof ctx.localVariables === 'string' ? parseIfJSON(ctx.localVariables) : (ctx.localVariables || {});
  // Form input values are exposed as bare `{{fieldId}}` tokens, where fieldId is
  // the input's id. Lowest precedence: local + design-time variables override a
  // form field on a name clash (variables are the authoritative source).
  const varsCtx = { ...(ctx.formValues || {}), ...(parsedLocal || {}), ...(ctx.variables || {}) };
  const thisCtx = ctx.thisContext;

  const result: Record<string, any> = {};
  chunks.forEach((chunk) => {
    (chunk.variables || []).forEach((row) => {
      const rawKey = (row.key || '').trim();
      if (!rawKey) return;

      // Unwrap a fully-wrapped key (`{{a.b[0]}}`) — the key is a destination
      // path, not a variable reference — then resolve any inner templates.
      let keyExpr = rawKey;
      if (keyExpr.startsWith('{{') && keyExpr.endsWith('}}')) {
        keyExpr = keyExpr.slice(2, -2).trim();
      }
      const resolvedKey = resolveTemplateRecursive(keyExpr, varsCtx, thisCtx);
      if (!resolvedKey) return;

      // Page init (routes/page/index.tsx) runs the WHOLE screen — including these
      // event-variable rows — through replacePlaceholdersWithVariables, which
      // resolves every `{{...}}` in a `value` field against the variable bag that
      // exists AT LOAD. Form values are empty then, so a `{{field}}` reference is
      // baked into '' and the source template is preserved in `valueOriginal`.
      // Recover from valueOriginal so the token survives until the event actually
      // fires. Guard with the same {{...}} test the action saga uses (sagas.ts):
      // only treat valueOriginal as the source when it genuinely carries a
      // template — otherwise honour the (possibly intentionally literal) value.
      const hasTemplateOriginal = typeof row.valueOriginal === 'string' && /\{\{[^}]+\}\}/.test(row.valueOriginal);
      const rawValue = hasTemplateOriginal ? (row.valueOriginal as string) : (row.value ?? '');
      const resolvedValue = resolveTemplateRecursive(rawValue, varsCtx, thisCtx);
      const typedValue = convertToType(resolvedValue, row.type);

      setByPath(result, resolvedKey, typedValue);
    });
  });
  return result;
};
